<script lang="ts" setup>
import { DetailComp, GridComp, DataSourceLookup, AlertMessage, InputSelect } from '@/components';
import { useEventCommonStore, useEventStore, useUtilStore, useEventCommonStoreRef, useRaceContestantTimeStore } from '@/stores';
import { Participant, RaceContestant, RaceContestantDetails } from '@/objects/generated';
import { computed, onMounted, ref, watch } from 'vue';
import { FieldAlertMessage, type SelectLine } from '@/objects';
import { GridColumnDefinition, GridColumnOptions, GridDataSource, GridSetup } from '@/components/Grid/Classes';
import type { IGridRowGetterParams } from '@/components/Grid/Classes/GridDataSource';
import { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import RaceContestantExitStatusLookup from '@/components/RaceContestant/RaceContestantExitStatusLookup.vue';
import RaceContestantExitStatusSelect from '@/components/RaceContestant/RaceContestantExitStatusSelect.vue';
import type { ComponentExposed } from 'vue-component-type-helpers';
import ButtonComp from '@/components/Basic/ButtonComp.vue';
import ContestantBibAndChips from './ContestantBibAndChips.vue';
import ContestantFirstAndSurname from './ContestantFirstAndSurname.vue';
import { propertyof } from '@/utilities';
import ContestantRelayTeam from './ContestantRelayTeam.vue';
import type DetailViewExpose from '@/objects/DetailViewExpose';
import { RaceContestantDetailsGuiDef } from '@/objects/generated/gui';
import { findAvailableCategoriesForGenderAndBirthdayInCurrentEvent, getEventAgeForCurrentEvent } from '@/utilities/categoryutils';
import { $ti, $tm } from '@/locize';
import { watchDebounced } from '@vueuse/core';
import type { RaceContestantId } from '@/utilities/typealiases';

const eventStore = useEventStore();
const comStore = useEventCommonStore();

const props = defineProps<{
    /**
     * If the contestant are part of atleast one Race with relay settings
     */
    isRelayRace: boolean;

    /**
     * If we should open the participant search dialog on load
     */
    openParticipantSearch?: boolean;

    /**
     * Default raceid for the new race contestant
     */
    raceId?: number;

    /**
     * If there is a start time that should be prefilled when creating a new racecontestant
     */
    prefillStartTime?: Date;
}>();

/**
 * The value model for the contestant
 */
const contestant = defineModel<RaceContestantDetails>({ required: true });

const hasRaceContestantsThatAreNotDeleted = computed(
    () => (contestant.value?.RaceContestants.filter((x) => x.EntityState != 'Deleted')?.length ?? 0) === 0,
);

/**
 * References to commonly used stores
 */
const { Categories, Groups, Races } = useEventCommonStoreRef();

/**
 * We need to check if there is any times for the current racecontestant
 */
const { GetTimesForRaceContestantId } = useRaceContestantTimeStore();

/**
 * The grid component reference for the race contestants
 */
const raceContestantsGridComp = ref<ComponentExposed<typeof GridComp<RaceContestant>> | null>(null);

/**
 * Reference to the ContestantFirstAndSurname component
 */
const contestantFirstAndSurname = ref<ComponentExposed<typeof ContestantFirstAndSurname> | null>(null);

// We need to keep track of the content element so we can access some of the children
const content = ref<HTMLElement | null>(null);

/**
 * Reference to the DetailComp for the RaceContestantDetails
 */
const detailComp = ref<{ messages: FieldAlertMessage[] } | null>(null);

/**
 * A list of all the racecontestant ids that has errors we use this one to highlight the cell in the grid
 *
 */
const raceContestantGroupErrors = new Set<RaceContestantId>();

// Set up the columns for the racecontestant grid
let columns: GridColumnOptions<RaceContestant>[] = [];
new GridColumnOptionsBuilder<RaceContestant>(propertyof<RaceContestant>('RaceID'), 'general.race')
    .CellEditor(InputSelect, { datasource: { mapsource: useEventCommonStore().RacesAsSelectLine } })
    .CellRenderer(DataSourceLookup, { datasource: { mapsource: useEventCommonStore().RacesAsSelectLine } })
    .InsertAtEnd(columns);

new GridColumnOptionsBuilder<RaceContestant>(propertyof<RaceContestant>('GroupID'), 'general.group')
    .CellEditor(InputSelect, {
        datasource: (row: RaceContestant) => {
            return useEventCommonStore()
                .Groups.filter((x) => x.RaceID == row.RaceID)
                .map((x) => ({ value: x.ID, text: x.Name }) as SelectLine);
        },
    })
    .CellRenderer(DataSourceLookup, { hideNotfound: true, datasource: { mapsource: useEventCommonStore().GroupsAsSelectLine } })
    .CssRule({
        className: 'grid-cell-warning',
        func: (val: any, row: RaceContestant) => {
            return row.GroupID === -1 || raceContestantGroupErrors.has(row.ID);
        },
    })
    .InsertAtEnd(columns);

new GridColumnOptionsBuilder<RaceContestant>(propertyof<RaceContestant>('DNS'), 'racecontestant.exitstatus')
    .CellRenderer(RaceContestantExitStatusLookup)
    .CellEditor(RaceContestantExitStatusSelect)
    .InsertAtEnd(columns);

new GridColumnOptionsBuilder<RaceContestant>(
    propertyof<RaceContestant>('FlagTime'),
    'racecontestant.flagtime',
    undefined,
    'datetime',
).InsertAtEnd(columns);
new GridColumnOptionsBuilder<RaceContestant>(propertyof<RaceContestant>('ExitReason'), 'racecontestant.exitreason', undefined, 'string')
    .Size(240)
    .InsertAtEnd(columns);

new GridColumnOptionsBuilder<RaceContestant>(
    propertyof<RaceContestant>('AlternativeBib'),
    'racecontestant.alternativebib',
    undefined,
    'number',
).InsertAtEnd(columns);
new GridColumnOptionsBuilder<RaceContestant>(
    propertyof<RaceContestant>('AlternativeChip1'),
    'racecontestant.alternativechip1',
    undefined,
    'string',
).InsertAtEnd(columns);
new GridColumnOptionsBuilder<RaceContestant>(
    propertyof<RaceContestant>('AlternativeChip2'),
    'racecontestant.alternativechip2',
    undefined,
    'string',
).InsertAtEnd(columns);

/**
 * We add a new racecontestant to the dataset
 */
const addRacecontestant = () => {
    // We default to the first race
    let raceId = comStore.Races[0]?.ID ?? 0;

    // We try and find the first group with this raceId
    let groupId = comStore.Groups.find((x) => x.RaceID == raceId)?.ID ?? 0;

    const rc = RaceContestant.FromJsValues({
        EntityState: 'Added',
        EventID: eventStore.eventID,
        ContestantID: contestant.value.ContestantID,
        ID: useUtilStore().nextFakeId(), // We need to add a unique negative id
        RaceID: raceId,
        GroupID: groupId,
    });

    contestant.value.RaceContestants = [...contestant.value.RaceContestants, rc];
    raceContestantsGridComp.value?.refreshCachedData();
};

const deleteSelectedRacecontestants = () => {
    let selected = raceContestantsGridComp.value?.getSelectedRows();

    if (selected) {
        selected.forEach((x) => {
            x.Delete();
        });
        raceContestantsGridComp.value?.refreshCachedData();
    }
};

/**
 * Handle the lock state event
 * @param lock
 */
const handleLockState = (lock: boolean) => {
    // We need to set all the inputs to readonly
    content.value?.querySelectorAll('.is-lockable').forEach((x) => {
        if (lock && !x.classList.contains('unlocked')) {
            (x as HTMLInputElement).setAttribute('disabled', 'disabled');
        } else if (!lock) {
            (x as HTMLInputElement).removeAttribute('disabled');
        }

        // Find all the input types with the class is-lockable
        x.querySelectorAll('input, select, textarea, button').forEach((x) => {
            // Add the readonly attribute
            if (lock && !x.classList.contains('unlocked')) {
                (x as HTMLInputElement).setAttribute('disabled', 'disabled');
            } else if (!lock) {
                (x as HTMLInputElement).removeAttribute('disabled');
            }
        });
    });
};

/**
 * The grid setup for the racecontestants grid
 */
const gridSetup = new GridSetup<RaceContestant>({
    rowIdGetter: (row: RaceContestant) => row.ID.toString(),
    hasCheckboxSelectionColumn: true,

    rowClassRules: [
        {
            className: 'ag-row-added',
            func: (row: RaceContestant) => row.EntityState === 'Added',
        },
        {
            className: 'grid-row-success',
            func: (row: RaceContestant) => GetTimesForRaceContestantId(row.ID).length > 0,
        },
    ],
    datasource: new GridDataSource({
        rowGetter: (p: IGridRowGetterParams<RaceContestant>) => {
            // Ignore the deleted rows
            let data = contestant.value.RaceContestants.filter((x) => x.EntityState != 'Deleted');

            p?.sort(data);
            const slice = data?.slice(p.startRow, p.endRow);
            return p.successCallback(slice, data, data.length);
        },
    }),
    rowSelection: 'single',

    GridColumnDefinition: new GridColumnDefinition({
        CommonColumnOptions: { editable: true, suppressFillHandle: true, sortable: false },
        ColumnOptions: columns,
    }),
});

/**
 * The calculated filtered categories for the current contestant
 * This is the list that should be shown in the category select
 */
const filteredCategories = computed<SelectLine[]>(() => {
    if (contestant.value?.Birthday == null || contestant.value?.Gender == null) {
        return [];
    }

    // we select race for first added raceContestant if there is one
    const raceId = contestant.value
        ? [...contestant.value.RaceContestants].sort((a, b) => a.ID - b.ID).find((x) => x.EntityState != 'Deleted')?.RaceID
        : undefined;

    // This is a list of all the category ids that are alloved for the current gender and birthday
    const matchingCategoryIds = findAvailableCategoriesForGenderAndBirthdayInCurrentEvent({
        birthday: contestant.value.EventAge == null ? contestant.value.Birthday : null,
        gender: contestant.value.Gender,
        age: contestant.value.EventAge,
        raceId: raceId,
    });

    // We need to check if the original category is part of the new list, if it is not, we should never use it
    const originalCatIsNotValid = matchingCategoryIds.includes(contestant.value.CategoryID ?? -1) == false;

    // if the original category is not valid, it means it is not in the list, we still add it so it is visible in the select but it should be disabled
    if (originalCatIsNotValid && contestant.value.CategoryID != null) {
        matchingCategoryIds.push(contestant.value.CategoryID);
    }

    // We set an error message if the original category is not valid

    const res = Categories.value
        .filter((x) => matchingCategoryIds.includes(x.ID))
        .map((x) => ({ value: x.ID, text: x.Name, disabled: originalCatIsNotValid && x.ID == contestant.value.CategoryID }));

    return res;
});

// Contains any category error message
const categoryErrorMessage = ref<FieldAlertMessage | null>(null);

// Contains a warning message to show. This will NOT block the save function, but should be noted by the user
const categoryWarningMessage = ref<FieldAlertMessage | null>(null);

// Contains any racecontestant error message
const raceContestantErrorMessage = ref<FieldAlertMessage | null>(null);

/**
 * We watch the filtered categories and set an error message if there are no categories available
 */
watchDebounced(
    () => filteredCategories.value,
    () => {
        const currentOption = filteredCategories.value.find((x) => x.value == contestant.value.CategoryID);

        if (currentOption?.disabled) {
            categoryErrorMessage.value = buildFieldAlertMessage(
                'racecontestant.currentcategorynotavailable',
                'warning',
                contestant.value,
                contestant.value.CategoryID ?? undefined,
            );
        } else if (currentOption?.disabled || filteredCategories.value?.filter((x) => !x.disabled).length == 0) {
            categoryErrorMessage.value = buildFieldAlertMessage(
                'racecontestant.nocategoryavailable',
                'danger',
                contestant.value,
                contestant.value.CategoryID ?? undefined,
            );
        } else {
            categoryErrorMessage.value = null;

            if (!currentOption) {
                contestant.value.CategoryID = (filteredCategories.value.find((x) => !x.disabled)?.value ?? null) as number | null;
            }
        }
    },
    { debounce: 100 },
);

/**
 * Callback for providing the GuiDef with the correct fields for the DetailComp
 * Here we can manipulate the ones we need to change
 */
const getRaceContestantGuiDef = () => {
    // We need to handle the CategoryID field
    let guidef = new RaceContestantDetailsGuiDef();

    guidef.Fields = guidef.Fields?.filter((x) => !(x.Collapsable !== null && x.Collapsable.Collapsable == 'contestantaddress'));

    // We need to find the category field and override the datasoure with a filtered verison
    const category = guidef.Fields?.find((x) => x.FieldName == propertyof<RaceContestantDetails>('CategoryID'));
    if (category) {
        category.DetailEditorProps = { datasource: { source: filteredCategories } as any };
    }

    return guidef;
};

/**
 * Whenever the category changes we need to check if there is any changes that needs to be done on the RaceContestants
 * @param newid
 * @param oldid
 */
const handleCategoryChanges = (newid: number | null, oldid: number | null) => {
    // We need to check if the selected category has the correct gender
    const category = Categories.value.find((x) => x.ID == newid);
    const contestantAge =
        contestant.value.EventAge ?? contestant.value.Age ?? getEventAgeForCurrentEvent(contestant.value.Birthday ?? null) ?? 0;

    if (category && props.isRelayRace == false) {
        let categoryMessage: string | null = null;

        let genderMismatch = category.Gender === 'n' ? false : category.Gender != contestant.value.Gender;
        let ageMismatch = contestantAge < (category.AgeFrom ?? 0) || contestantAge > (category.AgeTo ?? 0);

        if (genderMismatch && ageMismatch) {
            categoryMessage = 'racecontestant.categorydoesnotmatchgenderandage';
        } else if (genderMismatch) {
            categoryMessage = 'racecontestant.categorydoesnotmatchgender';
        } else if (ageMismatch) {
            categoryMessage = 'racecontestant.categorydoesnotmatchage';
        }

        if (categoryMessage) {
            categoryErrorMessage.value = buildFieldAlertMessage(categoryMessage, 'warning', contestant.value, category.ID);
        } else {
            categoryErrorMessage.value = null;
        }
    }

    const groupIds = Categories.value.find((x) => x.ID == newid)?.CategoryGroupConnection.map((x) => x.GroupID) ?? [];
    const groups = Groups.value.filter((x) => groupIds.includes(x.ID));

    if (newid === null || groups == null || groups.length == 0) {
        return;
    }

    // We need to know what RaceContestants allready have times
    // The only times we should add a new racecontestant automatically is if there are no racecontestants. If there are any race contestants, you have to add it manually.
    if (groups.length > 0 && contestant.value.RaceContestants.filter((x) => x.EntityState != 'Deleted').length == 0) {
        const rc = RaceContestant.FromJsValues({
            EntityState: 'Added',
            EventID: eventStore.eventID,
            ContestantID: contestant.value.ContestantID,
            ID: useUtilStore().nextFakeId(), // We need to add a unique negative id
            StartingTime: props.prefillStartTime ?? null,
        });

        contestant.value.RaceContestants = [...contestant.value.RaceContestants, rc];
        raceContestantsGridComp.value?.refreshCachedData();
    }

    // We iterate all the race contestants and check for the following.
    // 1: If they have id <= 0 it means they have not been saved yet and can be removed without any checks
    // 2: If they have id > 0 we need to check if there are any times for them, if there are we cannot remove them or update them. This should trigger an error message to the user. I think we also add a
    //     row-success for the row so it shows as green in the grid
    // 3: If they have no times we can safely update the RaceID and GroupID to match the new category groups

    contestant.value.RaceContestants.forEach((rc) => {
        const race = Races.value.find((x) => x.ID == rc.RaceID);

        if (race && (race?.Level > 1 || race?.Run > 1)) {
            // We skip the check for higher level races
            return;
        }

        // If id <= 0 we can remove it or update it freely
        if (rc.ID <= 0) {
            // We set the IDs to the first valid group for the category
            // We can update it freely

            rc.RaceID = props.raceId ?? groups[0].RaceID;

            let group = Groups.value.find((x) => x.RaceID == rc.RaceID && groupIds.includes(x.ID));
            rc.GroupID = group?.ID ?? groups[0].ID;
        } else {
            // We need to make sure it does not have any times
            const times = GetTimesForRaceContestantId(rc.ID);
            if (times.length == 0) {
                if (groups.length == 1) {
                    rc.RaceID = groups[0].RaceID;
                    rc.GroupID = groups[0].ID;
                } else {
                    // We can update it freely
                    rc.RaceID = rc.RaceID ?? groups[0].RaceID;
                    rc.GroupID = rc.GroupID ?? groups[0].ID;
                }
            }
        }
    });

    // Since we now can end up in a situation where the Category does NOT match any race contestant, we should also show that as an error message
    // If we dont have any race contestants with the valid group - category connection, we need to raise an error.

    const hasValidConnection = contestant.value.RaceContestants.some((rc) => {
        const group = Groups.value.find((x) => x.ID == rc.GroupID);
        return group && group.CategoryGroupConnection.map((x) => x.CategoryID).includes(newid);
    });

    if (!hasValidConnection) {
        raceContestantErrorMessage.value = buildFieldAlertMessage(
            'racecontestant.noracecontestantvalidforcategory',
            'danger',
            contestant.value,
            newid,
        );
    }

    // Last we check if there is a mismatch between the contestant age + gender and the category that is set. This will only give a warning message
    // initial check to see if the category is valid.
    if ((contestant.value?.ID ?? 0) <= 0) {
        // New contestant, we skip the check
        return;
    }

    // We check if the current category is part of the available categories

    if (!!contestant.value?.CategoryID && !filteredCategories.value.find((x) => x.value == newid && !x.disabled)) {
        categoryWarningMessage.value = buildFieldAlertMessage(
            'racecontestant.categorydoesnotmatch',
            'warning',
            contestant.value,
            contestant.value.CategoryID ?? undefined,
        );
    } else {
        categoryWarningMessage.value = null;
    }

    raceContestantsGridComp.value?.refreshCells();
};

/**
 * Helper function to build a FieldAlertMessage
 * @param translationkey
 * @param type
 * @param contestant
 * @param categoryId
 * @param groupId
 * @param raceId
 */
const buildFieldAlertMessage = (
    translationkey: string,
    type: 'danger' | 'warning' | 'success',
    contestant?: RaceContestantDetails,
    categoryId?: number,
    groupId?: number,
    raceId?: number,
) => {
    return new FieldAlertMessage(
        'racecontestant',
        $ti(translationkey, {
            category: Categories.value.find((x) => x.ID == (categoryId ?? 0))?.Name ?? '',
            group: Groups.value.find((x) => x.ID == (groupId ?? 0))?.Name ?? '',
            race: Races.value.find((x) => x.ID == (raceId ?? 0))?.Name ?? '',
            gender: contestant?.Gender == 'm' ? $tm('genders.male', '', false) : $tm('genders.female', '', false),
            age: (contestant?.EventAge ?? getEventAgeForCurrentEvent(contestant?.Birthday ?? null))?.toString() ?? '-',
        }),
        type,
    );
};

/**
 * We will handle every change in the racecontestants here
 * Important changes is to validate that the current contestant category is valid for the racecontestants or else mark them as errors
 * If there is a 1 to 1 between a race and a category we will automatically change the category
 * @param nv
 * @param ov
 */
const handleRaceContestantChanged = (nv: RaceContestant[], ov: RaceContestant[]) => {
    let error: FieldAlertMessage | null = null;

    // If there is only one raceContestant, we can check what has changed and
    // try and change the category to a new one if there is only 1 category that is available for the current racecontestants
    if (nv.length == 1) {
        const matchingCategories =
            findAvailableCategoriesForGenderAndBirthdayInCurrentEvent({
                gender: contestant.value.Gender,
                birthday: contestant.value.EventAge == null ? contestant.value.Birthday : null,
                age: contestant.value.EventAge,
            }) ?? null;

        nv.forEach((rc) => {
            if (rc.EntityState != 'Modified' && rc.EntityState != 'Unchanged') return;

            // If there is no matching category we can just set the category to null and return
            if (matchingCategories == null) {
                contestant.value.CategoryID = null;
                return;
            }

            const stateMap = raceContestantStateMap.get(rc.ID);

            if (!stateMap || stateMap.length != 2) {
                return;
            }

            const oldRaceId = stateMap[0];
            const oldGroupId = stateMap[1];

            // if there is a change in the race, we will try and find a group that matches the new race
            if (rc.RaceID != oldRaceId) {
                const matchingGroups = Groups.value.filter(
                    (x) => x.RaceID == rc.RaceID && x.CategoryGroupConnection.some((cgc) => matchingCategories.includes(cgc.CategoryID)),
                );

                if (matchingGroups.length == 1) {
                    rc.GroupID = matchingGroups[0].ID;
                } else {
                    // We set it to null so it is not valid anymore
                    rc.GroupID = -1;
                }
            }

            // If the group id has changed we need to find the category that are connected to this group and check if there is only one category that is connected to this group, if there is we will automatically change the category to match that category
            if (rc.GroupID != oldGroupId && rc.GroupID != -1) {
                const group = Groups.value.find((x) => x.ID == rc.GroupID);
                if (group) {
                    // We need to find the categories that match the contestant

                    // This is all the categories that are available for the current race/group connection
                    const categoryIds = group.CategoryGroupConnection.map((x) => x.CategoryID);

                    // We need to filter the matchingCategories down to the ones that are in the categoryIds list
                    const possibleCategoryIds = matchingCategories?.filter((x) => categoryIds.includes(x));

                    // If we only have one possible category we will automatically change the category to match that category
                    if (possibleCategoryIds.length == 1) {
                        contestant.value.CategoryID = possibleCategoryIds[0];
                    }
                }
            }
        });
    }

    // We need to find the current category for the contestant
    const currentCategory = Categories.value.find((x) => x.ID == contestant.value.CategoryID) ?? null;

    // The category should have a group connection with the given race, if there is NO group connection we assume it matches all groups and therefore are valid
    if (currentCategory === null || currentCategory.CategoryGroupConnection?.length == 0) {
        raceContestantErrorMessage.value = null;
        return;
    }

    // First we need to check if the current GroupID is valid for the current RaceID on each racecontestant. If it is not, we will remove the GroupID and mark the cell as invalid
    contestant.value.RaceContestants.forEach((rc) => {
        if (rc.RaceID === null || rc.EntityState === 'Deleted') {
            return;
        }

        const group = Groups.value.find((x) => x.RaceID == rc.RaceID && x.ID == rc.GroupID);
        if (!group) {
            // It is invalid so we set it to -1 which is invalid
            rc.GroupID = -1;
        }
    });

    // It has group connections so we need to validate it by checking all the racecontestants that they are part of the groupconnections
    let groupIds = currentCategory.CategoryGroupConnection.map((x) => x.GroupID);
    let raceIds = Groups.value.filter((x) => groupIds.includes(x.ID)).map((x) => x.RaceID);

    // We need to check if the raceId and the groupId are valid for the current category

    // We iterate the racecontestants
    let translationkey: string | null = null;
    contestant.value.RaceContestants.forEach((rc) => {
        raceContestantGroupErrors.delete(rc.ID);
        // We need to check if the current category has group connections. If it does not we assume it is valid for all groups
        let hasValidConnection = true;
        if (currentCategory.CategoryGroupConnection?.length > 0) {
            const currentGroup = Groups.value.find((x) => x.ID == rc.GroupID);
            hasValidConnection = currentGroup?.CategoryGroupConnection.find((x) => x.CategoryID == currentCategory.ID) != null;
        }

        if (!hasValidConnection) {
            translationkey = 'racecontestant.groupnotvalidforcategory';
        } else if (rc.EntityState !== 'Deleted' && rc.RaceID !== null && !raceIds.includes(rc.RaceID)) {
            translationkey = 'racecontestant.nocategoryavailableforgroup';
        }

        if (translationkey != null) {
            raceContestantGroupErrors.add(rc.ID);
            error = buildFieldAlertMessage(
                translationkey,
                'warning',
                contestant.value,
                currentCategory.ID,
                rc.GroupID ?? undefined,
                rc.RaceID ?? undefined,
            );
        }
    });

    raceContestantErrorMessage.value = error;

    raceContestantsGridComp.value?.refreshCachedData();
};

/**
 * Will be raised when we find a new participant from the participant search
 * @param participant
 */
const onParticipantSelected = (participant: Participant) => {
    console.log('Participant selected', participant);
};

/**
 * Triggers whenever the category changes
 */
watch(
    () => contestant.value?.CategoryID,
    (nv, ov) => {
        // This is important, if you open a contestant that has a pulje that is not allowed it will change it,
        // this is NOT WANTED. It should not change what is already set
        if (ov === undefined && contestant.value != null && contestant.value.ID > 0) {
            // This is the initial run of the watch, we should not trigger the handleCategoryChanges function here since it will be triggered by the watch on the racecontestants
            // TODO: We should trigger validation here
            return;
        }

        handleCategoryChanges(nv, ov ?? null);
    },
    { immediate: true },
);

const raceContestantStateMap = new Map<number, number[]>();

/**
 * This is a deep watch to check for any changes in the racecontestants
 * When there is a change we will need to handle and validate it
 */
watch(
    contestant.value.RaceContestants,
    (nv, ov) => {
        handleRaceContestantChanged(nv, ov);

        // We set the new state in the map
        contestant.value.RaceContestants.forEach((rc) => {
            raceContestantStateMap.set(rc.ID, [rc.RaceID ?? -1, rc.GroupID ?? -1]);
        });
    },
    { deep: true },
);

/**
 * We need to watch the contestant gender and date of birth to preselect the category if it is not set
 */
watch(
    () => [contestant.value?.Gender, contestant.value?.Birthday],
    () => {
        // If there is no category selected we try and select one
        if (
            contestant.value?.CategoryID == null &&
            contestant.value &&
            contestant.value.Birthday != null &&
            contestant.value.Gender != null &&
            contestant.value.EventAge == null // This mean that it is a new contestant and the birthday has just been set
        ) {
            contestant.value.CategoryID =
                findAvailableCategoriesForGenderAndBirthdayInCurrentEvent({
                    gender: contestant.value.Gender,
                    birthday: contestant.value.EventAge == null ? contestant.value.Birthday : null,
                    age: contestant.value.EventAge,
                }).at(0) ?? null;
        }
    },
    { immediate: true },
);

/**
 * If the relay team name is set
 */
const isRelayTeamSet = computed(() => {
    return contestant.value?.RelayTeamName != null && contestant.value.RelayTeamName.trim() != '';
});

onMounted(() => {
    // We save the current RaceID, and GroupID for each racecontestant every time they change
    contestant.value.RaceContestants.forEach((rc) => {
        raceContestantStateMap.set(rc.ID, [rc.RaceID ?? -1, rc.GroupID ?? -1]);
    });
});

/**
 * If we set a relay team, we remove the first name and surname
 */
watch(isRelayTeamSet, (nv) => {
    if (nv) {
        // We check if the participant id is set, if it is not we clear the names
        // If there is a participant ID that is most likely for the person that signed up the team. so that one we should not change.
        // But note...  That mean that the historical results will be connected to that person
        if (!contestant.value.ParticipantID) {
            contestant.value.Firstname = '';
            contestant.value.Surname = '';
            // We default the birthday to 1899-12-31
            contestant.value.Birthday = new Date(1899, 11, 31);
        }
    }
});

defineExpose<DetailViewExpose<RaceContestantDetails> & { dataUpdated: () => void; ShowParticipantSearch: () => void }>({
    val: computed(() => contestant.value),
    title: computed(() => ''),
    changed: computed(() => false),
    messages: computed(() => {
        const messages: FieldAlertMessage[] = [];

        if (categoryErrorMessage.value) {
            messages.push(categoryErrorMessage.value);
        }

        if (raceContestantErrorMessage.value) {
            messages.push(raceContestantErrorMessage.value);
        }

        if ((contestantFirstAndSurname.value?.messages ?? []).length > 0) {
            contestantFirstAndSurname.value?.messages!.forEach((element) => {
                messages.push(element);
            });
        }

        if (detailComp.value?.messages && detailComp.value?.messages.length > 0) {
            detailComp.value?.messages.forEach((element) => {
                messages.push(element);
            });
        }

        if (categoryWarningMessage.value) {
            messages.push(categoryWarningMessage.value);
        }

        return messages;
    }),
    dataUpdated: () => {
        raceContestantsGridComp.value?.purgeCachedData();
    },

    /**
     * Open the participant search dialog in the ContestantFirstAndSurname component
     */
    ShowParticipantSearch: () => contestantFirstAndSurname.value?.ShowParticipantSearch(),
});
</script>
<template>
    <div
        class="d-flex flex-column flex-fill gap-2"
        ref="content"
    >
        <ContestantRelayTeam
            v-model="contestant"
            v-if="isRelayRace"
        />

        <ContestantFirstAndSurname
            ref="contestantFirstAndSurname"
            @lockstate="handleLockState"
            @participantselected="onParticipantSelected"
            v-model="contestant"
            :isRelayRace="isRelayRace"
            :open-participant-search="openParticipantSearch"
            :locked="isRelayTeamSet"
        />
        <DetailComp
            :gui="getRaceContestantGuiDef()"
            v-model="contestant"
            ref="detailComp"
        />

        <div class="form-separator flex-fill">
            <hr />
            <div>
                <span>{{ $tm('racecontestant.bibandchips') }}</span>
            </div>
        </div>

        <ContestantBibAndChips v-model="contestant" />

        <div class="form-separator flex-fill">
            <hr />
            <div>
                <span>{{ $tm('general.racecontestant') }}</span>
            </div>
        </div>

        <div class="d-flex gap-2">
            <ButtonComp
                button-type="Add"
                @click="addRacecontestant"
            >
                {{ $tm('button.add') }}
            </ButtonComp>
            <ButtonComp
                button-type="Delete"
                @click="deleteSelectedRacecontestants"
            >
                {{ $tm('button.delete') }}
            </ButtonComp>
        </div>

        <GridComp
            ref="raceContestantsGridComp"
            resize-strategy="sizeColumnsToFit"
            :grid-setup="gridSetup"
        />

        <AlertMessage v-if="hasRaceContestantsThatAreNotDeleted">
            {{ $tm('racecontestant.noracecontestants') }}
        </AlertMessage>
    </div>
</template>
